import { formatDate } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import {
  BehaviorSubject,
  filter,
  finalize,
  Observable,
  switchMap,
  take,
} from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { SpecialCharacters } from 'src/app/core/enums';
import { CurrentLocation } from 'src/app/core/models/admin/location-admin.model';
import { UserAdmin } from 'src/app/core/models/admin/user-admin.model';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { FileUploadResponse } from 'src/app/core/models/file-upload-results.model';
import { WarehouseOutbound } from 'src/app/core/models/inbound/warehouse-scan';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { WarehouseOutboundService } from 'src/app/core/services/inbound/warehouse-outbound/warehouse-outbound.service';
import { InterceptsService } from 'src/app/core/services/position-operations/intercepts-service/intercepts-service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { Intercept } from '../../../shared/intercept';
import {
  FileParseType,
  FileReturn,
} from '../../file-upload/file-upload.constants';
import {
  IMAGE_SRC,
  NO_RESULTS,
  VALIDATION_MESSAGE,
} from '../../shared-constants';
import {
  ColumnIds,
  ColumnText,
  CONST,
  SORT_OPTION_LIST,
} from './warehouse-outbound-table.constant';

@Component({
  selector: 'app-warehouse-outbound-table',
  templateUrl: './warehouse-outbound-table.component.html',
  styleUrls: ['./warehouse-outbound-table.component.scss'],
  standalone: false,
})
export class WarehouseOutboundTableComponent
  extends Intercept
  implements OnInit
{
  readonly _warehouseOutboundService = inject(WarehouseOutboundService);
  readonly _commonUtils = inject(CommonUtils);
  readonly _interceptService = inject(InterceptsService);
  readonly _dialog = inject(MatDialog);
  private readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);
  private locationService = inject(LocationService);

  readonly currentLocation = signal<CurrentLocation | undefined>(undefined);
  readonly currentUser = signal<UserAdmin | undefined>(undefined);

  readonly SRC = IMAGE_SRC;
  readonly CONST = { ...CONST, ...VALIDATION_MESSAGE };
  readonly COLUMNTEXT = ColumnText;
  readonly NO_RESULTS = NO_RESULTS;
  readonly displayedColumns = Object.values(ColumnIds);
  readonly PARSE_TYPE = FileParseType;

  readonly SORT_OPTION_LIST = SORT_OPTION_LIST;
  readonly SpecialCharacters = SpecialCharacters;

  dataSource = new MatTableDataSource<WarehouseOutbound>();
  form!: FormGroup;
  dropdownOpen = [false];
  isFileUpload: boolean = false;

  // Create column search objects
  createTableColumn(columnId: string): TableColumn {
    return {
      itemId: columnId,
      ids: [],
      formControl: new FormControl(''),
    };
  }

  // Create admin table columns
  trackingNumberData = this.createTableColumn(ColumnIds.TRACKING_NUMBER);
  sortDate = this.createTableColumn(ColumnIds.SORT_DATE);
  sortCode = this.createTableColumn(ColumnIds.SORT_CODE);

  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  sortCodeOption: Items[] = [];
  warehouseScan: WarehouseOutbound[] = [];

  constructor(
    public commonUtils: CommonUtils,
    private readonly fb: FormBuilder,
  ) {
    super();
  }

  /**
   * OnInit Lifecycle hook
   *
   * @memberof WarehouseOutboundTableComponent
   */
  ngOnInit(): void {
    this.form = this.fb.group({
      sortDate: [new Date(), Validators.required],
      sortCode: [null, Validators.required],
      trackingNumbers: ['', [Validators.required]],
    });

    this.dataSource.filterPredicate = this.createFilter();
    this.getCurrentUser();
    this.getCurrentLocation();
  }

  /**
   * get current user
   *
   * @memberof HandlingScanTableComponent
   */
  getCurrentUser() {
    this.userService
      .getCurrentUser$()
      .pipe(
        filter((user) => !!user),
        takeUntilDestroyed(this.destroy),
        switchMap((_user) => {
          this.currentUser.set(_user);
          return this.getIntercepts();
        }),
      )
      .subscribe();
  }

  getCurrentLocation() {
    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((val) => {
        this.currentLocation.set(val);
        this.resetState();
      });
  }

  resetState() {
    this.dataSource.data = [];
    this.sortCodeOption = [];
    this.form.reset({
      sortDate: new Date(),
    });
  }

  /**
   * used to set code option
   *
   * @memberof WarehouseOutboundTableComponent
   */
  setCodeOption() {
    this.dataSource.data.forEach((item) => {
      const isReasonTypeAdded = this.sortCodeOption.find(
        (i) => i.id === item.sortCode,
      );
      if (!isReasonTypeAdded) {
        this.sortCodeOption.push({
          id: item.sortCode,
          selected: true,
        });
      }
    });
  }

  /**
   *
   * Create filter for table
   * @private
   * @return {*}  {(data: any, filter: string) => boolean}
   * @memberof WarehouseOutboundTableComponent
   */
  private createFilter(): (data: WarehouseOutbound, filter: string) => boolean {
    return (data: WarehouseOutbound, filter: string) => {
      const filters = JSON.parse(filter);

      return Object.keys(filters).every((filterKey) => {
        //Allows opening of row if filters are applied already
        if (filters[filterKey] === '') {
          return true;
        }
        return this.filterByColumn(filterKey, data, filters);
      });
    };
  }

  /**
   * Filter column by column id
   *
   * @private
   * @param {string} filterKey
   * @param {*} data
   * @param {*} filters
   * @return {*}
   * @memberof WarehouseOutboundTableComponent
   */
  private filterByColumn(
    filterKey: string,
    data: WarehouseOutbound,
    filters: WarehouseOutbound,
  ) {
    switch (filterKey) {
      case ColumnIds.SORT_DATE:
        if (filters.sortDate !== '') {
          const filteredStartDate = new Date(filters.sortDate);
          const dataDate = new Date(data.sortDate);
          if (dataDate.getTime() === filteredStartDate.getTime()) {
            return true;
          }
        }
        return false;
      case ColumnIds.SORT_CODE: {
        const value = filters[filterKey];
        const rowValue = data[filterKey];
        const searchData = rowValue?.toString().toLowerCase() || '';
        const searchValue = value?.toString().toLowerCase();
        if (searchValue) {
          return searchValue.includes(searchData);
        }
        return searchData.includes(searchValue);
      }
      default: {
        const value = filters[filterKey as keyof WarehouseOutbound];
        const rowValue = data[filterKey as keyof WarehouseOutbound];
        const searchData = rowValue?.toString().toLowerCase() || '';
        const searchValue = value?.toString().toLowerCase();
        return searchData.includes(searchValue);
      }
    }
  }

  /**
   * Triggers when a dropdown option is checked/unchecked
   *
   * @param {*} $event
   * @param {AdminTableColumn} field
   * @memberof WarehouseOutboundTableComponent
   */
  onStatusChangeForDropdowns($event: string | string[], field: TableColumn) {
    field.ids[0] = $event as unknown as Items;
    if (field.itemId === ColumnIds.SORT_CODE) {
      this.sortCode.formControl.setValue($event);
    }
    this.search();
  }

  /**
   * Filtering through the search bar
   *
   * @memberof WarehouseOutboundTableComponent
   */
  search() {
    this.commonUtils.searchFilter(
      [this.trackingNumberData, this.sortDate],
      this.dataSource,
      [this.sortCode],
    );
  }

  /**
   * Clear all the filters if user has searched/filtered any column
   *
   * @memberof WarehouseOutboundTableComponent
   */
  clearAllFilters() {
    this.commonUtils.clearAllFilters(this.dataSource, this.warehouseScan, [
      this.trackingNumberData.formControl,
      this.sortDate.formControl,
      this.sortCode.formControl,
    ]);
    this.resetSelectDropdowns.next(true);
  }

  /**
   * Figure out if filter button is enabled
   * Should not be enabled if user has not filtered any column
   *
   * @readonly
   * @memberof WarehouseOutboundTableComponent
   */
  get isFilterEnabled() {
    return (
      this.trackingNumberData.formControl.value !== '' ||
      this.sortDate.formControl.value !== '' ||
      this.sortCode.formControl.value !== ''
    );
  }

  submit(fileData: FileReturn, isFileUpload: boolean) {
    this.isFileUpload = isFileUpload;

    const trackingNumber = fileData.returnData
      .flatMap((val) => val)
      .map((val) => val.trackingNumber);

    const intercept = this.intercepts().filter((item) =>
      trackingNumber.includes(item.trackingNumber),
    );

    const apis$ = fileData.returnData?.map((record) => {
      const request = {
        sortCode: this.form.get('sortCode')?.value,
        sortDate: formatDate(
          this.form.get('sortDate')?.value,
          'yyyy-MM-dd',
          'en-US',
        ),
        trackingNumbers: record.map((val) => val.trackingNumber),
        fromFileUpload: this.isFileUpload,
      };
      return {
        numbers: record.map((val) => val.trackingNumber),
        call$: this._warehouseOutboundService.outboundScan(request),
      };
    });

    if (intercept.length > 0) {
      this.commonUtils.updateInterceptError(
        intercept
          .map((val) => val.trackingNumber)
          .slice(0, 4)
          .toString(),
      );
    } else {
      this.upload(apis$, fileData);
    }
  }

  private upload(
    apis$: {
      numbers: string[];
      call$: Observable<FileUploadResponse>;
    }[],
    fileData: FileReturn,
  ) {
    this.commonUtils.updateLoadingModal(true);

    if (this.isFileUpload) {
      this.commonUtils
        .runFileUploadCallsInChunks(
          apis$,
          this.commonUtils.generateUploadString(
            new Date(),
            this.currentLocation()?.location?.locationDetails.code || '',
            this.currentUser()?.loginId || '',
          ),
          fileData.fileName,
          fileData.fileSize,
        )
        .subscribe((succeeded) => {
          this.commonUtils.updateLoadingModal(false);
          if (succeeded) {
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
          }
        });
    } else {
      apis$[0].call$
        .pipe(
          take(1),
          finalize(() => {
            this.commonUtils.updateLoadingModal(false);
          }),
        )
        .subscribe((val) => {
          if (!!val?.response?.whseScans) {
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            this.warehouseScan = [
              ...this.warehouseScan,
              ...((val.response?.whseScans as WarehouseOutbound[]) || []),
            ];
            const tableData = this.dataSource.data;
            this.dataSource.data = [...this.warehouseScan, ...tableData];
          }
        });
    }
  }
}
